/* F15 STEP 2 — BORN LINKED, THE WEB'S HALF (2026-10-30, `docs/F15-COACH-ENGINE-DESIGN.md` §2.3 · §7 step 2).
 *
 * 🔴 A PORT OF `HybridTrainer/Services/AI/CatalogLink.swift` (`key` · `literalKey` · `depluralised` · `resolve` · `atSave`),
 * and the ONE rule every web save of a coach exercise runs. It is held to the Swift by a CORPUS the harness emits from the
 * Swift itself (`catalogLink.generated.ts`, gate `f15-2-born-linked`): every published catalogue name, every starter name and
 * the names that broke a matcher before, each with the verdict the Swift gives. `catalogLink.test.ts` asserts this port
 * reproduces every one — a change to either side that the other does not make is red.
 *
 * ⚠️ THE TIER BOUNDARY IS THE SWIFT'S SAFETY RULE: tier 1/2 (one row by folded name, or once `DB`/`KB` expand) LINKS;
 * containment is only ever a QUESTION and is never written by a save.
 */
import { ABBREVIATIONS } from "./exerciseVocabulary";

export type CatalogRow = { readonly id: string; readonly name: string };
export type How = "exactName" | "abbreviation";
export type Verdict =
  | { readonly kind: "linked"; readonly id: string; readonly how: How }
  | { readonly kind: "candidates"; readonly ids: readonly string[] }
  | { readonly kind: "novel" };

/** A link and its provenance — the pair `coach_exercises_catalog_link_has_source` keeps together. */
export type Link = { readonly id: string; readonly source: string };

/** The two provenances only a machine writes; every other source (`coach`, `fable-…`) is a decision a save keeps. */
export const MACHINE_SOURCES: ReadonlySet<string> = new Set(["name-exact", "name-abbreviation"]);

export function sourceOf(how: How): string {
  return how === "exactName" ? "name-exact" : "name-abbreviation";
}

/** Swift's `isLetter || isNumber` per character. */
const WORD = /[\p{L}\p{N}]/u;
/** Swift's `CharacterSet.alphanumerics`: letters, marks and numbers. */
const ALNUM = /[\p{L}\p{M}\p{N}]/u;

export function replacingWholeToken(token: string, expansion: string, s: string): string {
  let out = "";
  let current = "";
  for (const ch of s) {
    if (WORD.test(ch)) current += ch;
    else { out += current === token ? expansion : current; out += ch; current = ""; }
  }
  return out + (current === token ? expansion : current);
}

function alnumOnly(s: string): string {
  let out = "";
  for (const ch of s) if (ALNUM.test(ch)) out += ch;
  return out;
}

/** ONE trailing `s`, never `ss`, never on a key of three letters or fewer. */
export function depluralised(key: string): string {
  if ([...key].length <= 3 || !key.endsWith("s") || key.endsWith("ss")) return key;
  return key.slice(0, -1);
}

export function catalogKey(name: string): string {
  let lower = name.toLowerCase();
  for (const { token, expansion } of ABBREVIATIONS) lower = replacingWholeToken(token, expansion, lower);
  return depluralised(alnumOnly(lower));
}

export function literalKey(name: string): string {
  return depluralised(alnumOnly(name.toLowerCase()));
}

/** Shortest key first, then name, then id — the Swift's stated order (code-unit compare, as Swift's `<` on ASCII). */
function ordered(rows: readonly CatalogRow[]): string[] {
  const cmp = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);
  return [...rows].sort((a, b) => {
    const ka = [...catalogKey(a.name)].length, kb = [...catalogKey(b.name)].length;
    if (ka !== kb) return ka - kb;
    if (a.name !== b.name) return cmp(a.name, b.name);
    return cmp(a.id.toUpperCase(), b.id.toUpperCase());
  }).map((r) => r.id);
}

export function resolve(coachName: string, catalogue: readonly CatalogRow[]): Verdict {
  const literal = literalKey(coachName);
  const expanded = catalogKey(coachName);
  if (literal === "") return { kind: "novel" };

  const exact = catalogue.filter((r) => literalKey(r.name) === literal);
  if (exact.length === 1) return { kind: "linked", id: exact[0]!.id, how: "exactName" };
  if (exact.length > 1) return { kind: "candidates", ids: ordered(exact) };

  const viaAbbrev = catalogue.filter((r) => catalogKey(r.name) === expanded);
  if (viaAbbrev.length === 1) return { kind: "linked", id: viaAbbrev[0]!.id, how: "abbreviation" };
  if (viaAbbrev.length > 1) return { kind: "candidates", ids: ordered(viaAbbrev) };

  const near = catalogue.filter((r) => {
    const k = catalogKey(r.name);
    return k !== "" && (k.includes(expanded) || expanded.includes(k));
  });
  return near.length === 0 ? { kind: "novel" } : { kind: "candidates", ids: ordered(near) };
}

/** `CatalogLink.atSave`, step for step: a decided link is kept; a coach's no writes none; otherwise tier 1/2 answers. */
export function linkAtSave(name: string, current: Link | null, declined: boolean,
                           catalogue: readonly CatalogRow[]): Link | null {
  if (current !== null && !MACHINE_SOURCES.has(current.source)) return current;
  if (declined) return null;
  const v = resolve(name, catalogue);
  return v.kind === "linked" ? { id: v.id, source: sourceOf(v.how) } : null;
}

/** 🔴 THE EXERCISE EDITOR'S LINK AT SAVE — the phone form's rename rule, then `linkAtSave`. The editor has no picker, so the
 *  only link it holds is the one the row OPENED with; once the name moves away from the opened name, that link describes
 *  a different exercise and is dropped (the phone's `refreshMatch` does the same), and tier 1/2 answers for the new name. */
export function editorLink(name: string, openedName: string | null, opened: Link | null,
                           catalogue: readonly CatalogRow[]): Link | null {
  const renamed = openedName !== null && name.trim() !== openedName.trim();
  return linkAtSave(name.trim(), renamed ? null : opened, false, catalogue);
}

/** The pair as written — both columns or neither, never one. */
export function linkColumns(link: Link | null): { catalog_exercise_id: string | null; catalog_link_source: string | null } {
  return { catalog_exercise_id: link?.id ?? null, catalog_link_source: link?.source ?? null };
}
